import { useEffect } from 'react'
import { useLazyMeQuery } from 'state/services/user/user'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import { useUserData } from 'state/user/hooks'
import {
  accessTokenLocalStorageKey,
  areStringsEqual,
  ConnectorNameEnum,
  ExternalNameEnum,
  userIdLocalStorageKey,
  walletLocalStorageKey
} from '@mprolab/sdk'
import isNil from 'lodash/isNil'
import isEmpty from 'lodash/isEmpty'
import useAuth from './useAuth'

const ACCESS_TOKEN_EXPIRATION_TIME = parseInt(import.meta.env.VITE_ACCESS_TOKEN_EXPIRATION_TIME || '0')

export const useMe = () => {
  const { account, isActive, isActivating, connector } = useWeb3React()
  const localUserId = localStorage.getItem(userIdLocalStorageKey)
  const localAccessToken = localStorage.getItem(accessTokenLocalStorageKey)
  const localWallet = localStorage.getItem(walletLocalStorageKey)
  const { clearData, setLoading, userData } = useUserData()
  const { logout } = useAuth()
  const [fetchUser, { isError }] = useLazyMeQuery({
    refetchOnReconnect: true,
    pollingInterval: ACCESS_TOKEN_EXPIRATION_TIME
  })

  useEffect(() => {
    ;(async () => {
      if (!isNil(account) && userData?.addresses.some((address) => !isEmpty(address.wallet))) {
        const loggedUser = userData?.addresses.some((address) => areStringsEqual(address.wallet, account))
        // If the logged-in wallet does not match the current account, log out the user
        if (!loggedUser) {
          logout({ connector, autoDisconnect: true })
          return // Exit the function to prevent further execution
        }
      }
      if (
        [
          ConnectorNameEnum.METAMASK,
          ConnectorNameEnum.TRUSTWALLET,
          ConnectorNameEnum.WALLETCONNECT
        ].includes(localWallet as ConnectorNameEnum)
      ) {
        if (account && isActive && localAccessToken && localWallet && localUserId)
          await fetchUser({ userId: localUserId, accessToken: localAccessToken, account })
      } else if (
        localWallet === ExternalNameEnum.STEAM ||
        localWallet === ExternalNameEnum.GOOGLE ||
        localWallet === ExternalNameEnum.APPLE ||
        localWallet === ExternalNameEnum.DISCORD ||
        localWallet === ExternalNameEnum.TELEGRAM
      ) {
        if (localAccessToken && localWallet && localUserId)
          await fetchUser({ userId: localUserId, accessToken: localAccessToken })
      }
      setLoading(false)
    })()
  }, [account, isActive, localWallet, localAccessToken, isActivating])

  useEffect(() => {
    if (isError) clearData()
  }, [account, isActive, isError])
}
